import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedId: string | null;
  impersonatedName: string | null;
  impersonatedRole: "provider" | "company" | null;
  sessionId?: string | null;
  expiresAt?: string | null;
  remainingMinutes?: number | null;
  viewingAsRole: "super_admin" | "admin" | "company" | "provider" | null;
}

interface ImpersonationContextType extends ImpersonationState {
  startImpersonation: (id: string, name: string, role: "provider" | "company") => Promise<void>;
  endImpersonation: () => Promise<void>;
  startRoleView: (role: "super_admin" | "admin" | "company" | "provider") => Promise<void>;
  endRoleView: () => Promise<void>;
  confirmEditAction: (
    action: "create" | "update" | "delete",
    table: string,
    recordId?: string,
    details?: { old_value?: any; new_value?: any }
  ) => Promise<boolean>;
  canEdit: boolean;
  actualRole: string | null;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<ImpersonationState>({
    isImpersonating: false,
    impersonatedId: null,
    impersonatedName: null,
    impersonatedRole: null,
    sessionId: null,
    expiresAt: null,
    remainingMinutes: null,
    viewingAsRole: null,
  });
  const [adminRole, setAdminRole] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    action: string;
    table: string;
    recordId?: string;
    details?: any;
    resolve: (value: boolean) => void;
  } | null>(null);

  useEffect(() => {
    // Fetch impersonation state from server-side RPC (secure)
    const fetchImpersonationState = async () => {
      try {
        const { data, error } = await supabase.rpc('get_impersonation_state');
        
        if (error) {
          console.error("Error fetching impersonation state:", error);
          return;
        }

        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const impersonationData = data as {
            success?: boolean;
            isImpersonating?: boolean;
            impersonatedId?: string;
            impersonatedName?: string;
            impersonatedRole?: "provider" | "company";
            adminRole?: string;
            sessionId?: string;
            expiresAt?: string;
            remainingMinutes?: number;
          };
          
          // Check if session is expiring soon (less than 5 minutes)
          if (impersonationData.remainingMinutes && impersonationData.remainingMinutes < 5 && impersonationData.remainingMinutes > 0) {
            setShowExpiryWarning(true);
          }
          
          setState({
            isImpersonating: impersonationData.isImpersonating || false,
            impersonatedId: impersonationData.impersonatedId || null,
            impersonatedName: impersonationData.impersonatedName || null,
            impersonatedRole: impersonationData.impersonatedRole || null,
            sessionId: impersonationData.sessionId || null,
            expiresAt: impersonationData.expiresAt || null,
            remainingMinutes: impersonationData.remainingMinutes || null,
            viewingAsRole: null,
          });
          setAdminRole(impersonationData.adminRole || null);
        }
      } catch (error) {
        console.error("Error fetching impersonation state:", error);
      }
    };

    fetchImpersonationState();
    
    // Poll for session expiry every 30 seconds
    const interval = setInterval(fetchImpersonationState, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const startImpersonation = async (
    id: string,
    name: string,
    role: "provider" | "company"
  ) => {
    try {
      // Call server-side RPC to start impersonation (secure, validated)
      const { data, error } = await supabase.rpc('start_impersonation', {
        target_user_id: id,
        target_role: role,
        target_name: name,
      });

      if (error) {
        console.error("Error starting impersonation:", error);
        throw error;
      }

      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const impersonationData = data as {
          success?: boolean;
          isImpersonating?: boolean;
          impersonatedId?: string;
          impersonatedName?: string;
          impersonatedRole?: "provider" | "company";
          adminRole?: string;
          sessionId?: string;
          expiresAt?: string;
          remainingMinutes?: number;
          error?: string;
          message?: string;
        };
        
        // Check for errors (e.g., rate limiting)
        if (impersonationData.success === false) {
          throw new Error(impersonationData.message || 'Failed to start impersonation');
        }
        
        // Reset expiry warning for new session
        setShowExpiryWarning(false);
        
        setState({
          isImpersonating: impersonationData.isImpersonating || false,
          impersonatedId: impersonationData.impersonatedId || null,
          impersonatedName: impersonationData.impersonatedName || null,
          impersonatedRole: impersonationData.impersonatedRole || null,
          sessionId: impersonationData.sessionId || null,
          expiresAt: impersonationData.expiresAt || null,
          remainingMinutes: impersonationData.remainingMinutes || null,
          viewingAsRole: null,
        });
        setAdminRole(impersonationData.adminRole || null);
      }
    } catch (error) {
      console.error("Error starting impersonation:", error);
      throw error;
    }
  };

  const endImpersonation = async () => {
    try {
      // Call server-side RPC to end impersonation (secure, validated)
      const { data, error } = await supabase.rpc('end_impersonation');

      if (error) {
        console.error("Error ending impersonation:", error);
        throw error;
      }

      setShowExpiryWarning(false);
      setState({
        isImpersonating: false,
        impersonatedId: null,
        impersonatedName: null,
        impersonatedRole: null,
        sessionId: null,
        expiresAt: null,
        remainingMinutes: null,
        viewingAsRole: null,
      });
    } catch (error) {
      console.error("Error ending impersonation:", error);
      throw error;
    }
  };

  const confirmEditAction = async (
    action: "create" | "update" | "delete",
    table: string,
    recordId?: string,
    details?: { old_value?: any; new_value?: any }
  ): Promise<boolean> => {
    if (!state.isImpersonating) return true;

    // Only super_admins can edit during impersonation
    if (adminRole !== "super_admin") {
      return false;
    }

    return new Promise((resolve) => {
      setPendingAction({ action, table, recordId, details, resolve });
      setShowConfirmDialog(true);
    });
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Log the action
      await supabase.from("audit_log").insert({
        admin_id: user.id,
        impersonated_id: state.impersonatedId,
        role_type: state.impersonatedRole,
        action: `${pendingAction.action}_during_impersonation`,
        affected_table: pendingAction.table,
        record_id: pendingAction.recordId,
        details: pendingAction.details,
        user_id: state.impersonatedId || user.id,
        changed_by: user.id,
        timestamp: new Date().toISOString(),
      });

      pendingAction.resolve(true);
    } catch (error) {
      console.error("Error logging impersonation action:", error);
      pendingAction.resolve(false);
    } finally {
      setShowConfirmDialog(false);
      setPendingAction(null);
    }
  };

  const handleCancel = () => {
    if (pendingAction) {
      pendingAction.resolve(false);
    }
    setShowConfirmDialog(false);
    setPendingAction(null);
  };

  const startRoleView = async (role: "super_admin" | "admin" | "company" | "provider") => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Log role view start
      await supabase.from("audit_log").insert({
        admin_id: user.id,
        action: "role_view_started",
        details: { viewed_role: role },
        user_id: user.id,
        changed_by: user.id,
        timestamp: new Date().toISOString(),
      });

      setState(prev => ({ ...prev, viewingAsRole: role }));
    } catch (error) {
      console.error("Error starting role view:", error);
      throw error;
    }
  };

  const endRoleView = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Log role view end
      await supabase.from("audit_log").insert({
        admin_id: user.id,
        action: "role_view_ended",
        details: { viewed_role: state.viewingAsRole },
        user_id: user.id,
        changed_by: user.id,
        timestamp: new Date().toISOString(),
      });

      setState(prev => ({ ...prev, viewingAsRole: null }));
    } catch (error) {
      console.error("Error ending role view:", error);
      throw error;
    }
  };

  const canEdit = !state.isImpersonating || adminRole === "super_admin";

  return (
    <ImpersonationContext.Provider
      value={{
        ...state,
        startImpersonation,
        endImpersonation,
        startRoleView,
        endRoleView,
        confirmEditAction,
        canEdit,
        actualRole: adminRole,
      }}
    >
      {children}
      
      {/* Session Expiry Warning */}
      {showExpiryWarning && state.isImpersonating && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <div className="bg-yellow-100 dark:bg-yellow-900/40 p-2 rounded-full">
                <svg
                  className="h-5 w-5 text-yellow-600 dark:text-yellow-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm text-yellow-800 dark:text-yellow-200">
                  Sesjon utløper snart
                </h3>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  Din visningssesjon utløper om {Math.round(state.remainingMinutes || 0)} minutter. 
                  Økten avsluttes automatisk for din sikkerhet.
                </p>
                <button
                  onClick={() => setShowExpiryWarning(false)}
                  className="text-xs text-yellow-600 dark:text-yellow-400 hover:underline mt-2"
                >
                  Lukk
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Dialog */}
      {showConfirmDialog && pendingAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border border-destructive rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="bg-destructive/10 p-2 rounded-full">
                <svg
                  className="h-6 w-6 text-destructive"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">Bekreft endring under visning</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  ⚠️ Du er for øyeblikket logget inn som <strong>{state.impersonatedName}</strong> som admin.
                  Denne endringen vil bli logget under din admin-konto. Vil du fortsette?
                </p>
                <div className="bg-muted p-3 rounded text-xs space-y-1">
                  <div><strong>Handling:</strong> {pendingAction.action}</div>
                  <div><strong>Tabell:</strong> {pendingAction.table}</div>
                  {pendingAction.recordId && (
                    <div><strong>Post ID:</strong> {pendingAction.recordId}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors"
              >
                Bekreft endring
              </button>
            </div>
          </div>
        </div>
      )}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error("useImpersonation must be used within ImpersonationProvider");
  }
  return context;
};
